// Mobile (below-md) counterpart of supplier-row.tsx — one tappable card per supplier
// instead of a table row. Reuses the exact same props contract (ISupplierRow), the same
// lazily-fetched detail query, and the same expanded-section children (GmvByRestaurantTable,
// statements links, InvoicesList, CollectionPanel, SupplierOrdersModal); only the collapsed
// presentation differs. Desktop keeps the table untouched — the screen mounts exactly one of
// the two per breakpoint.
import { useGetReconciliationSupplierDetailsApi } from 'apis/reconciliation';
import dayjs from 'dayjs';
import { Box, Button, Flex, HStack, Icon, Link, SimpleGrid, Skeleton, Stack, Text } from 'design-system';
import { CollectionPanel } from 'pages/reconciliation/components/collection-panel';
import { GmvByRestaurantTable } from 'pages/reconciliation/components/gmv-by-restaurant-table';
import { InvoicesList } from 'pages/reconciliation/components/invoices-list';
import { SupplierOrdersModal } from 'pages/reconciliation/components/supplier-orders-modal';
import type { ISupplierRow } from 'pages/reconciliation/components/supplier-row';
import {
  formatAed,
  formatVariance,
  getStatusLabel,
  STATUS_META,
  varianceColor,
} from 'pages/reconciliation/utils/format';
import { ReactNode, useState } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { Link as RouterLink } from 'react-router-dom';

const MetricCell = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box minW={0}>
    <Text fontSize='10.5px' fontWeight='semibold' letterSpacing='0.08em' textTransform='uppercase' color='gray.500'>
      {label}
    </Text>
    {children}
  </Box>
);

export const SupplierCard = ({ row, month, isExpanded, onToggle, onEditSoa }: ISupplierRow) => {
  const restaurantsLabel = `${row.restaurantsCount} ${row.restaurantsCount === 1 ? 'restaurant' : 'restaurants'}`;

  const bd = row.commissionBreakdown;
  const totalOrders = bd ? bd.percentOrders + bd.fixedOrders + (bd.waivedOrders ?? 0) + bd.pendingTerms : null;
  const ordersLabel = totalOrders !== null ? `${totalOrders} ${totalOrders === 1 ? 'order' : 'orders'}` : null;
  const monthStart = `${month}-01`;
  const monthEnd = dayjs(monthStart).endOf('month').format('YYYY-MM-DD');

  // `?? open` mirrors getStatusLabel's fallback — see supplier-row.tsx.
  const statusMeta = STATUS_META[row.status] ?? STATUS_META.open;
  const statusLabel = getStatusLabel(row);

  // Same state-as-disclosure shape as supplier-row.tsx: the payload of which scope to open
  // doubles as the isOpen flag.
  const [ordersModal, setOrdersModal] = useState<{
    restaurantId: number | null;
    restaurantName: string | null;
    unpaidOnly: boolean;
  } | null>(null);

  // Only fetched once the card is expanded — the hook is a no-op (enabled: false) when supplierId is null.
  const {
    data: detail,
    isLoading: isDetailLoading,
    isError: isDetailError,
  } = useGetReconciliationSupplierDetailsApi(month, isExpanded ? row.supplierId : null);

  return (
    <Box bg='white' borderRadius='14px' border='1px solid' borderColor='gray.100' overflow='hidden'>
      <Box p='14px' cursor='pointer' onClick={onToggle} _hover={{ bg: '#FAFBFC' }}>
        <Flex align='flex-start' gap='sm'>
          <Box flex='1' minW={0}>
            <Text fontSize='15px' fontWeight='semibold' color='gray.700' noOfLines={2}>
              {row.supplierName}
            </Text>
            <Text fontSize='xs' color='gray.400' mt='2px' noOfLines={1}>
              {restaurantsLabel}
              {ordersLabel && ` · ${ordersLabel}`}
              {bd && bd.fixedOrders > 0 && ` · ${bd.fixedOrders} fixed`}
            </Text>
            {bd && bd.pendingTerms > 0 && (
              <Link
                as={RouterLink}
                to={`/app/gpo-orders/list?supplierId=${row.supplierId}&dateFrom=${monthStart}&dateTo=${monthEnd}`}
                onClick={(e) => e.stopPropagation()}
                fontSize='xs'
                color='orange.500'
                fontWeight='medium'
                whiteSpace='nowrap'>
                {bd.pendingTerms} missing commission
              </Link>
            )}
          </Box>

          <HStack spacing='xs' flexShrink={0} whiteSpace='nowrap' pt='2px'>
            <Box w='8px' h='8px' borderRadius='full' bg={statusMeta.dotColor} flexShrink={0} />
            <Text fontSize='xs' fontWeight='medium' color={statusMeta.textColor}>
              {statusLabel}
            </Text>
            <Icon
              as={FiChevronDown}
              color='gray.400'
              boxSize='14px'
              transform={isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)'}
              transition='transform 0.15s'
            />
          </HStack>
        </Flex>

        <SimpleGrid columns={2} spacingX='sm' spacingY='10px' mt='12px'>
          <MetricCell label='Platform GMV (AED)'>
            <Text fontSize='sm' fontWeight='medium' color='gray.700'>
              {formatAed(row.platformGmv)}
            </Text>
          </MetricCell>

          <MetricCell label='SOA GMV (AED)'>
            <Text fontSize='sm' fontWeight='medium' color={row.soaGmv === null ? 'gray.400' : 'gray.700'}>
              {row.soaGmv === null ? 'Not entered' : formatAed(row.soaGmv)}
            </Text>
          </MetricCell>

          <MetricCell label='Variance'>
            <Text fontSize='sm' fontWeight='semibold' color={varianceColor(row.variance)}>
              {formatVariance(row.variance)}
            </Text>
          </MetricCell>

          <MetricCell label='Revenue (AED)'>
            <Text fontSize='sm' fontWeight='medium' color='gray.700'>
              {formatAed(row.revenue)}
            </Text>
          </MetricCell>
        </SimpleGrid>
      </Box>

      {isExpanded && (
        <Box bg='#FAFBFC' borderTop='1px solid' borderColor='gray.100' p='sm'>
          {isDetailLoading ? (
            <Stack spacing='sm'>
              <Skeleton height='20px' />
              <Skeleton height='20px' />
              <Skeleton height='20px' />
            </Stack>
          ) : isDetailError || !detail ? (
            <Stack justify='center' minH='80px'>
              <Text color='gray.500' fontSize='sm' textAlign='center'>
                Could not load details
              </Text>
            </Stack>
          ) : (
            <Stack spacing='md'>
              <Box overflowX='auto'>
                <GmvByRestaurantTable
                  restaurants={detail.restaurants ?? []}
                  totals={{
                    platformGmv: row.platformGmv,
                    soaGmv: row.soaGmv,
                    variance: row.variance,
                    commission: row.revenue,
                  }}
                  showCommission={!!row.commissionBreakdown}
                  onViewOrders={({ restaurantId, restaurantName }) =>
                    setOrdersModal({ restaurantId, restaurantName, unpaidOnly: false })
                  }
                  headerAction={
                    // Hidden while closed — the SOA save endpoint would 409 on a closed month;
                    // reopen from the collection panel first.
                    row.status !== 'closed' ? (
                      <Button size='xs' variant='outline' onClick={onEditSoa}>
                        Edit SOA
                      </Button>
                    ) : undefined
                  }
                />
              </Box>

              {!!detail.documents.length && (
                <Stack spacing='xs' pt='sm' borderTop='1px solid' borderColor='gray.100'>
                  <Text
                    fontSize='2xs'
                    fontWeight='bold'
                    color='gray.500'
                    textTransform='uppercase'
                    letterSpacing='wide'>
                    Statements
                  </Text>
                  {detail.documents.map((doc) => (
                    <HStack key={doc.id} justify='space-between' spacing='sm'>
                      <Link
                        href={doc.url}
                        target='_blank'
                        rel='noopener noreferrer'
                        color='red.500'
                        fontSize='sm'
                        isTruncated>
                        {doc.fileName}
                      </Link>
                      <Text fontSize='xs' color='gray.500' whiteSpace='nowrap'>
                        {doc.restaurantName ?? 'Supplier-wide'}
                      </Text>
                    </HStack>
                  ))}
                </Stack>
              )}

              {!!detail.invoices?.length && <InvoicesList month={month} detail={detail} />}

              <Box pt='sm' borderTop='1px solid' borderColor='gray.100'>
                <CollectionPanel
                  month={month}
                  detail={detail}
                  onViewUnpaidOrders={() =>
                    setOrdersModal({ restaurantId: null, restaurantName: null, unpaidOnly: true })
                  }
                />
              </Box>
            </Stack>
          )}
        </Box>
      )}

      <SupplierOrdersModal
        isOpen={!!ordersModal}
        onClose={() => setOrdersModal(null)}
        month={month}
        supplierId={row.supplierId}
        supplierName={row.supplierName}
        restaurantId={ordersModal?.restaurantId ?? null}
        restaurantName={ordersModal?.restaurantName ?? null}
        defaultUnpaidOnly={ordersModal?.unpaidOnly ?? false}
      />
    </Box>
  );
};
